import { cn } from '@/utils/cn';

interface VeraLoaderProps {
  size?: 'sm' | 'md' | 'lg';
  fixed?: boolean;
  fullPage?: boolean;
  className?: string;
}

const sizeValues: Record<NonNullable<VeraLoaderProps['size']>, number> = {
  sm: 32,
  md: 48,
  lg: 64,
};

export const CLOVER_PATHS = {
  leaf1:
    'M18.4233 21.647C5.67954 25.0438 2.4381 21.314 1.23921 17.2289C0.550953 14.4981 1.06159 11.6118 2.4381 9.05865C3.97001 6.32784 6.34559 4.30749 9.23181 3.28622C13.1393 2.26494 18.0681 3.28621 21.4649 16.03C21.9756 18.0725 22.3086 19.4268 22.3086 19.7598C22.3086 19.9375 22.3086 20.0929 22.3086 20.2705C22.131 20.4481 22.131 20.6035 21.9756 20.6035C21.8202 20.6035 20.4659 20.9809 18.4233 21.647Z',
  leaf2:
    'M24.3691 26.4201C28.2765 40.8734 24.036 44.4479 19.4403 45.8022C16.3765 46.4905 12.9796 45.9798 10.2488 44.4479C7.18498 42.583 4.63179 39.8522 3.78813 36.7883C2.58924 32.3702 3.78813 26.9308 18.0638 23.0233C20.284 22.335 21.9713 22.002 22.1489 22.002C22.3265 22.002 22.4819 22.002 22.6595 22.1796C22.8371 22.3572 22.9926 22.5127 22.9926 22.6903C23.1702 22.4905 23.6808 24.0224 24.3691 26.4201Z',
  leaf3:
    'M28.2776 19.9596C40.6884 16.7182 43.7522 20.2927 44.7735 24.2001C45.2841 26.9309 44.9511 29.6396 43.5746 32.0151C42.2203 34.5683 39.8447 36.6109 36.9363 37.6322C33.2064 38.6534 28.433 37.6322 25.2138 25.399C24.7032 23.5341 24.3701 22.0022 24.3701 21.8246C24.3701 21.647 24.3701 21.4915 24.3701 21.4915C24.5477 21.3139 24.5477 21.1585 24.7032 21.1585C24.8808 20.9809 26.2351 20.6257 28.2776 19.9596Z',
  leaf4:
    'M22.6577 16.0297C19.4385 4.30722 23.013 1.24339 26.7428 0.222118C29.296 -0.288521 32.0046 0.0445043 34.2248 1.42101C36.778 2.77531 38.6429 4.99547 39.4866 7.70408C40.5079 11.2785 39.4866 15.8743 27.7641 18.9159C25.8992 19.4266 24.5227 19.7596 24.3673 19.7596C24.1897 19.7596 24.0342 19.7596 24.0342 19.7596C23.8566 19.582 23.8566 19.582 23.7012 19.4266C23.5236 19.2711 23.1684 17.9168 22.6577 16.0297Z',
  stem: 'M33.7154 48.0001C34.0485 48.0001 34.4037 47.8225 34.7367 47.667C35.0697 47.4894 35.2474 47.1564 35.425 46.8234C35.6026 46.4904 35.425 46.1351 35.425 45.8021C35.2474 45.4691 35.0919 45.1139 34.7367 44.9584C32.5166 43.6041 29.1197 40.0297 28.6091 37.8095C28.6091 37.6319 28.4315 37.4765 28.4315 37.4765C28.2538 37.2989 28.0984 37.2989 28.0984 37.4765C28.0984 37.4765 27.9208 37.4765 27.9208 37.6541L27.7432 37.8317V38.1869C28.2538 40.7401 29.7858 45.8465 33.1826 48.0445C33.0272 47.8447 33.3824 48.0001 33.7154 48.0001Z',
};

export const VeraLoader = ({
  size,
  fixed = false,
  fullPage = false,
  className,
}: VeraLoaderProps) => {
  const resolvedSize = size ?? (fullPage ? 'lg' : 'md');
  const pixels = sizeValues[resolvedSize];
  const partClass = fixed ? 'static-clover-part' : 'animated-clover-part';

  return (
    <div
      className={cn(
        'flex items-center justify-center',
        fullPage && 'min-h-[calc(100dvh-8rem)]',
        className,
      )}
    >
      <svg
        width={pixels}
        height={pixels}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="clover-svg transition-all"
        role="img"
        aria-label="Actual"
      >
        <title>Actual</title>
        <g transform="translate(48 0) scale(-1 1)">
          <path
            d={CLOVER_PATHS.leaf1}
            fill="currentColor"
            className={`${partClass} leaf-1`}
          />
          <path
            d={CLOVER_PATHS.leaf2}
            fill="currentColor"
            className={`${partClass} leaf-2`}
          />
          <path
            d={CLOVER_PATHS.leaf3}
            fill="currentColor"
            fillOpacity="0.9"
            className={`${partClass} leaf-3`}
          />
          <path
            d={CLOVER_PATHS.leaf4}
            fill="currentColor"
            className={`${partClass} leaf-4`}
          />
          <path
            d={CLOVER_PATHS.stem}
            fill="currentColor"
            fillOpacity="0.9"
            className={`${partClass} stem`}
          />
        </g>
      </svg>
    </div>
  );
};
